import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ContractIqRow from './ContractIqRow';
import { User } from '@/hooks/identity/responseModels/user';
import { FieldVerificationTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import styles from './contract-iq-table.module.scss';

export type SortColumn = 'fields' | 'type' | 'reasonCode' | 'verifiedAt' | 'updated' | 'user' | 'comment';
export type SortDirection = 'asc' | 'desc';

export interface FieldVerificationData {
  fieldVerificationID: string;
  fields: (string | null)[];
  reasonCode: string;
  verifiedAt: string;
  userID: string;
  comment?: string | null;
  type: FieldVerificationTypeEnum;
  updated?: string | null;
}

interface IContractIqTableProps {
  verifications: FieldVerificationData[];
  users: User[];
  reasonCodeNames: Record<string, string>;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onColumnSort: (column: SortColumn) => void;
  columnWidths: number[] | null;
  onColumnResize: (widths: number[]) => void;
  allUniqueFields: string[];
  selectedFieldFilters: string[];
  onFieldFilterChange: (fields: string[]) => void;
  currentUserId: string | null;
  onRemoveVerification: (verification: FieldVerificationData) => void;
}

interface ColumnDef {
  key: SortColumn | 'actions';
  label: string;
  className: string;
  minWidth: number;
}

const BASE_COLUMNS: ColumnDef[] = [
  { key: 'type', label: 'Type', className: 'col-type', minWidth: 90 },
  { key: 'fields', label: 'Fields', className: 'col-fields', minWidth: 140 },
  { key: 'reasonCode', label: 'Reason Name', className: 'col-reason', minWidth: 100 },
  { key: 'verifiedAt', label: 'Date', className: 'col-timestamp', minWidth: 120 },
  { key: 'user', label: 'User', className: 'col-user', minWidth: 100 },
  { key: 'comment', label: 'Notes', className: 'col-notes', minWidth: 100 },
  { key: 'actions', label: '', className: 'col-actions', minWidth: 80 },
];

const REMOVED_AT_COLUMN: ColumnDef = {
  key: 'updated', label: 'Removed At', className: 'col-removed-at', minWidth: 120,
};

export default function ContractIqTable({
  verifications,
  users,
  reasonCodeNames,
  sortColumn,
  sortDirection,
  onColumnSort,
  columnWidths,
  onColumnResize,
  allUniqueFields,
  selectedFieldFilters,
  onFieldFilterChange,
  currentUserId,
  onRemoveVerification,
}: IContractIqTableProps) {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const filterIconRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target as Node) &&
        filterIconRef.current &&
        !filterIconRef.current.contains(e.target as Node)
      ) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  const toggleFieldFilter = (field: string) => {
    const updated = selectedFieldFilters.includes(field)
      ? selectedFieldFilters.filter((f) => f !== field)
      : [...selectedFieldFilters, field];
    onFieldFilterChange(updated);
  };

  const clearAllFilters = () => {
    onFieldFilterChange([]);
  };

  const hasRemovedEntry = verifications.some(
    (v) => v.type === FieldVerificationTypeEnum.REMOVED,
  );

  const columns = useMemo(() => {
    if (!hasRemovedEntry) return BASE_COLUMNS;
    const cols = [...BASE_COLUMNS];
    const dateIdx = cols.findIndex((c) => c.key === 'verifiedAt');
    cols.splice(dateIdx + 1, 0, REMOVED_AT_COLUMN);
    return cols;
  }, [hasRemovedEntry]);

  const hasActiveFieldFilter = selectedFieldFilters.length > 0;
  const headerRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef<{ colIndex: number; startX: number; startWidths: number[] } | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  // Read actual column widths from the rendered header cells
  const measureWidths = useCallback((): number[] => {
    if (!headerRef.current) return [];
    const cells = headerRef.current.querySelectorAll(`.${styles['header-cell']}`);
    return Array.from(cells).map((cell) => (cell as HTMLElement).offsetWidth);
  }, []);

  const onResizeStart = useCallback(
    (colIndex: number, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Snapshot current widths (from state if available, else measure from DOM)
      const currentWidths = columnWidths ?? measureWidths();
      if (currentWidths.length === 0) return;
      resizingRef.current = {
        colIndex,
        startX: e.clientX,
        startWidths: [...currentWidths],
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [columnWidths, measureWidths],
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { colIndex, startX, startWidths } = resizingRef.current;
      const diff = e.clientX - startX;
      const minW = columns[colIndex].minWidth;
      const newWidths = [...startWidths];
      const proposed = Math.max(minW, startWidths[colIndex] + diff);
      const containerWidth = tableRef.current?.clientWidth ?? Infinity;
      const totalOthers = newWidths.reduce((s, w, i) => i === colIndex ? s : s + w, 0);
      newWidths[colIndex] = Math.min(proposed, containerWidth - totalOthers);
      onColumnResize(newWidths);
    };

    const onMouseUp = () => {
      if (resizingRef.current) {
        resizingRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [columns, onColumnResize]);

  // Only override grid template when user has resized columns
  const gridStyle = columnWidths
    ? { gridTemplateColumns: columnWidths.map((w) => `${w}px`).join(' ') }
    : undefined;

  return (
    <div className={styles.table} ref={tableRef}>
      {/* Table Header */}
      <div
        className={`${styles['table-header']} ${hasRemovedEntry ? styles['has-removed-column'] : ''}`}
        ref={headerRef}
        style={gridStyle}
      >
        {columns.map(({ key, label, className }, colIndex) => (
          <div
            key={key}
            className={`${styles[className]} ${styles['header-cell']}`}
          >
            <span
              className={styles['header-label']}
              onClick={() => key !== 'actions' ? onColumnSort(key as SortColumn) : undefined}
            >
              {label}
            </span>
            {sortColumn === key && (
              <svg
                className={styles['sort-arrow']}
                width="10"
                height="10"
                viewBox="0 0 10 10"
              >
                {sortDirection === 'asc'
                  ? <polyline points="2,7 5,3 8,7" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  : <polyline points="2,3 5,7 8,3" fill="none" stroke="currentColor" strokeWidth="1.5" />
                }
              </svg>
            )}
            {/* Filter icon for Fields Verified column */}
            {key === 'fields' && (
              <div className={styles['filter-wrapper']}>
                <button
                  ref={filterIconRef}
                  className={`${styles['filter-icon']} ${hasActiveFieldFilter ? styles['filter-active'] : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterDropdown((prev) => !prev);
                  }}
                  title="Filter fields"
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M1 2h14l-5.5 6.5V14l-3-2V8.5L1 2z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                      fill={hasActiveFieldFilter ? 'currentColor' : 'none'}
                    />
                  </svg>
                </button>
                {showFilterDropdown && (
                  <div ref={filterDropdownRef} className={styles['filter-dropdown']}>
                    <div className={styles['filter-dropdown-header']}>
                      <span className={styles['filter-dropdown-title']}>Filter by field</span>
                      {hasActiveFieldFilter && (
                        <button
                          className={styles['filter-clear-btn']}
                          onClick={clearAllFilters}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className={styles['filter-dropdown-list']}>
                      {allUniqueFields.map((field) => (
                        <label key={field} className={styles['filter-dropdown-item']}>
                          <input
                            type="checkbox"
                            checked={selectedFieldFilters.includes(field)}
                            onChange={() => toggleFieldFilter(field)}
                          />
                          <span>{field}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Resize handle */}
            {colIndex < columns.length - 1 && (
              <div
                className={styles['resize-handle']}
                onMouseDown={(e) => onResizeStart(colIndex, e)}
              />
            )}
          </div>
        ))}
      </div>

      <div>
        {verifications.map((verification, index) => (
          <ContractIqRow
            key={verification.fieldVerificationID}
            verification={verification}
            reasonCodeName={reasonCodeNames[verification.reasonCode] ?? verification.reasonCode}
            user={users.find(u => u.Id === verification.userID)}
            gridStyle={gridStyle}
            highlightedFields={selectedFieldFilters}
            index={index}
            currentUserId={currentUserId}
            showRemovedAtColumn={hasRemovedEntry}
            onRemove={() => onRemoveVerification(verification)}
          />
        ))}
      </div>
    </div>
  );
}
